import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Currency from "../../general/Currency";
import { useSurcharge, useTicket } from "../../../store/Store";
import { adjustmentTypes } from "../../general/constants";
import { getLabelTextForSurchargeItem } from "../../surcharge/api";
import { getBalanceDue, getTipsTotal } from "../../general/AppHelpers";
import Surcharge from "./Surcharge";

const PaymentSummary = () => {
  const { t } = useTranslation();
  const { data: ticketData } = useTicket();
  const { resource } = ticketData || {};
  const { balance_due, adjustments, discount_items, payment_items } =
    resource || {};

  const totalDiscounts = useMemo(() => {
    const promoTotal = discount_items?.reduce((sum, { amount }) => {
      const value = Number(amount);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    const giftCardTotal = payment_items
      ?.filter((item) => item?.payment?.code_type === "GiftCard")
      ?.reduce((sum, { payment }) => {
        const value = Number(payment?.amount);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);

    return promoTotal + giftCardTotal;
  }, [discount_items, payment_items]);

  // Calculate total tips from adjustments
  const totalTips = useMemo(() => getTipsTotal(ticketData), [ticketData]);

  // Calculate total taxes
  const totalTaxes = useMemo(() => {
    if (!adjustments || !Array.isArray(adjustments)) return 0;

    return adjustments
      ?.filter(
        (adj) => adj.adjustable_type === adjustmentTypes.tax && adj.amount > 0
      )
      ?.reduce((sum, tax) => sum + parseFloat(tax.amount), 0);
  }, [adjustments]);

  // Calculate net membership value credits (applied minus refunded)
  const totalMembershipValueCredits = useMemo(() => {
    if (!payment_items || !Array.isArray(payment_items)) return 0;

    return payment_items
      ?.filter((item) => item?.payment?.payment_type === "Pos::ClientCredit")
      ?.reduce((sum, { payment, refunds }) => {
        const appliedValue = Number(payment?.amount);
        const applied = isNaN(appliedValue) ? 0 : appliedValue;

        // Calculate refunds for this specific payment
        const refundTotal =
          refunds && Array.isArray(refunds)
            ? refunds
                ?.filter((refund) => refund?.payment_type === "client_credit")
                ?.reduce((refundSum, refund) => {
                  const value = Number(refund?.amount);
                  return refundSum + (isNaN(value) ? 0 : value);
                }, 0)
            : 0;

        // Return net amount (applied - refunded)
        return sum + (applied - refundTotal);
      }, 0);
  }, [payment_items]);

  const { data: surcharge } = useSurcharge();

  // What will actually be charged. Shown only when it differs from the balance
  // due, i.e. when the link pins a partial amount -- otherwise the "Balance Due"
  // row above already states it and a duplicate row is just confusing.
  const paymentAmount = useMemo(() => getBalanceDue(ticketData), [ticketData]);

  const surchargeLabel = useMemo(() => {
    if (surcharge && surcharge.label) return surcharge.label;
    else
      return getLabelTextForSurchargeItem(
        surcharge?.surcharge_configuration_item
      );
  }, [surcharge]);

  const totalAmount = useMemo(() => {
    return paymentAmount + surcharge?.surcharge_amount;
  }, [paymentAmount, surcharge]);

  return (
    <div className="space-y-2 text-sm">
      <h2 className="text-base font-medium text-black mb-2 text-lg">
        {t("paymentSummary.title")}
      </h2>

      {/* Balance Due */}
      <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
        <span className="text-black">{t("ticket.balance_due")}:</span>
        <span className="text-black">
          <Currency value={balance_due} />
        </span>
      </div>

      {/* Taxes */}
      {totalTaxes > 0 && (
        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
          <span className="text-black">{t("paymentSummary.taxes")}:</span>
          <span className="text-black">
            <Currency value={totalTaxes} />
          </span>
        </div>
      )}

      {/* Tips */}
      {totalTips > 0 && (
        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
          <span className="text-black">{t("paymentSummary.tips")}:</span>
          <span className="text-black">
            <Currency value={totalTips} />
          </span>
        </div>
      )}

      {/* Discounts */}
      {totalDiscounts > 0 && (
        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
          <span className="text-black">
            {t("ticketItemsSections.discounts")}:
          </span>
          <div className="text-sm text-red-600">
            -<Currency value={totalDiscounts} />
          </div>
        </div>
      )}

      {/* Membership Value Credits */}
      {totalMembershipValueCredits > 0 && (
        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
          <span className="text-black">
            {t("paymentSummary.membershipValueCredits")}:
          </span>
          <div className="text-sm text-red-600">
            -<Currency value={totalMembershipValueCredits} />
          </div>
        </div>
      )}

      {paymentAmount < balance_due && (
        <div className="flex justify-between items-center py-1.5">
          <span className="text-black">{t("ticket.payment_amount")}:</span>
          <span className="text-black">
            <Currency value={paymentAmount} />
          </span>
        </div>
      )}

      {surcharge && (
        <Surcharge
          surcharge={{
            label: surchargeLabel,
            amount: surcharge?.surcharge_amount,
          }}
          summary={true}
        />
      )}

      {surcharge && totalAmount > 0 && (
        <div className="flex justify-between items-center py-1.5">
          <span className="text-gray-600">{t("ticket.total")}:</span>
          <span className="font-medium text-gray-600">
            <Currency value={totalAmount} />
          </span>
        </div>
      )}
    </div>
  );
};

export default PaymentSummary;
